/**
 * camera.ts
 * Camera follow logic for Starfall Courier.
 *
 * Pure functions — no React, no side effects.
 * Called each fixed-step tick from gameLoop.ts after resolving Nova's position.
 *
 * Phase 1 — Movement Prototype.
 * See ROADMAP.md Phase 1 and ADR-002.
 *
 * @module camera
 */

import { CAMERA_LOOK_AHEAD, CAMERA_LERP, VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from '../../constants/gameConstants';

/** Camera state updated each tick. */
export interface CameraState {
  x: number;
  y: number;
}

/**
 * Compute the new camera position by lerping toward the target look-ahead point,
 * then clamping to level bounds.
 *
 * The target is Nova's centre offset by CAMERA_LOOK_AHEAD in the direction of travel,
 * minus half the viewport so the camera is centred on that point.
 *
 * @param camX        - Current camera X (world space, top-left).
 * @param camY        - Current camera Y (world space, top-left).
 * @param novaX       - Nova's X position (left edge of hitbox).
 * @param novaY       - Nova's Y position (top edge of hitbox).
 * @param novaWidth   - Nova's hitbox width (to compute centre).
 * @param novaHeight  - Nova's hitbox height (to compute centre).
 * @param facingRight - Whether Nova is facing right (determines look-ahead direction).
 * @param levelWidth  - Total level width in logical pixels.
 * @param levelHeight - Total level height in logical pixels.
 * @returns New clamped camera position.
 */
export function updateCamera(
  camX: number,
  camY: number,
  novaX: number,
  novaY: number,
  novaWidth: number,
  novaHeight: number,
  facingRight: boolean,
  levelWidth: number,
  levelHeight: number,
): CameraState {
  const novaCentreX = novaX + novaWidth / 2;
  const novaCentreY = novaY + novaHeight / 2;

  // DECISION: look-ahead applied to the horizontal target only — vertical look-ahead
  // is intentionally omitted to keep the horizon stable during jumps.
  const lookAheadDir = facingRight ? 1 : -1;
  const targetX = novaCentreX + lookAheadDir * CAMERA_LOOK_AHEAD - VIEWPORT_WIDTH / 2;
  const targetY = novaCentreY - VIEWPORT_HEIGHT / 2;

  // Lerp toward target for smooth follow
  const newX = camX + (targetX - camX) * CAMERA_LERP;
  const newY = camY + (targetY - camY) * CAMERA_LERP;

  // Clamp to level bounds so the camera never shows outside the level
  const clampedX = Math.max(0, Math.min(newX, levelWidth - VIEWPORT_WIDTH));
  const clampedY = Math.max(0, Math.min(newY, levelHeight - VIEWPORT_HEIGHT));

  return { x: clampedX, y: clampedY };
}

/**
 * Compute the camera position centered on the given coordinates, clamped to level bounds.
 * Used for instant camera repositioning (e.g., respawn to checkpoint).
 *
 * Unlike updateCamera(), this does NOT lerp — it returns the final clamped position immediately.
 *
 * @param novaX       - Nova's X position (left edge of hitbox).
 * @param novaY       - Nova's Y position (top edge of hitbox).
 * @param novaWidth   - Nova's hitbox width.
 * @param novaHeight  - Nova's hitbox height.
 * @param levelWidth  - Total level width in logical pixels.
 * @param levelHeight - Total level height in logical pixels.
 * @returns Clamped camera position centered on Nova.
 */
export function snapCameraToNova(
  novaX: number,
  novaY: number,
  novaWidth: number,
  novaHeight: number,
  levelWidth: number,
  levelHeight: number,
): CameraState {
  const novaCentreX = novaX + novaWidth / 2;
  const novaCentreY = novaY + novaHeight / 2;

  // Target position centred on Nova (no look-ahead for snap)
  const targetX = novaCentreX - VIEWPORT_WIDTH / 2;
  const targetY = novaCentreY - VIEWPORT_HEIGHT / 2;

  // Clamp to level bounds
  const clampedX = Math.max(0, Math.min(targetX, levelWidth - VIEWPORT_WIDTH));
  const clampedY = Math.max(0, Math.min(targetY, levelHeight - VIEWPORT_HEIGHT));

  return { x: clampedX, y: clampedY };
}
